import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";

const METRICS = [
  { value: "citations", label: "Citations" },
  { value: "h_index", label: "h-index" },
  { value: "i10_index", label: "i10-index" },
];

export const GoalDialog = ({ open, onOpenChange, onSave }) => {
  const [metric, setMetric] = useState("citations");
  const [target, setTarget] = useState("");
  const [deadline, setDeadline] = useState("");
  const [label, setLabel] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const reset = () => {
    setMetric("citations");
    setTarget("");
    setDeadline("");
    setLabel("");
    setNote("");
  };

  const submit = async (e) => {
    e.preventDefault();
    const t = parseInt(target, 10);
    if (!t || t <= 0 || !deadline) return;
    setSaving(true);
    try {
      await onSave({ metric, target: t, deadline, label, note });
      reset();
    } finally {
      setSaving(false);
    }
  };

  const today = new Date().toISOString().slice(0, 10);

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        onOpenChange(v);
        if (!v) reset();
      }}
    >
      <DialogContent
        className="sm:max-w-lg bg-white border border-ink/15 rounded-none p-0"
        data-testid="goal-dialog"
      >
        <form onSubmit={submit}>
          <DialogHeader className="p-6 pb-3 border-b border-paper-edge">
            <div className="overline text-saffron-hover mb-2">Set a milestone</div>
            <DialogTitle className="font-display text-3xl tracking-tight">
              New academic goal
            </DialogTitle>
            <DialogDescription className="text-ink-mute">
              Pick a metric, a target, and a date you&apos;d like to hit it by.
            </DialogDescription>
          </DialogHeader>

          <div className="p-6 space-y-5">
            <div>
              <div className="overline mb-2">Metric</div>
              <div className="grid grid-cols-3 gap-0 border border-ink/15">
                {METRICS.map((m) => (
                  <button
                    key={m.value}
                    type="button"
                    onClick={() => setMetric(m.value)}
                    data-testid={`goal-metric-${m.value}`}
                    className={`px-3 py-2.5 font-mono text-xs uppercase tracking-wider transition-colors ${
                      metric === m.value
                        ? "bg-ink text-paper"
                        : "bg-transparent text-ink-mute hover:text-ink"
                    }`}
                  >
                    {m.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="goal-target" className="overline mb-2 block">
                  Target value
                </label>
                <input
                  id="goal-target"
                  data-testid="goal-target-input"
                  type="number"
                  min="1"
                  inputMode="numeric"
                  value={target}
                  onChange={(e) => setTarget(e.target.value)}
                  placeholder="e.g. 150"
                  required
                  className="w-full border border-paper-edge bg-paper-alt px-3 py-2.5 font-mono text-sm focus:outline-none focus:border-ink/40"
                />
              </div>
              <div>
                <label htmlFor="goal-deadline" className="overline mb-2 block">
                  By date
                </label>
                <input
                  id="goal-deadline"
                  data-testid="goal-deadline-input"
                  type="date"
                  min={today}
                  value={deadline}
                  onChange={(e) => setDeadline(e.target.value)}
                  required
                  className="w-full border border-paper-edge bg-paper-alt px-3 py-2.5 font-mono text-sm focus:outline-none focus:border-ink/40"
                />
              </div>
            </div>

            <div>
              <label htmlFor="goal-label" className="overline mb-2 block">
                Label (optional)
              </label>
              <input
                id="goal-label"
                data-testid="goal-label-input"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder="e.g. Promotion review readiness"
                className="w-full border border-paper-edge bg-paper-alt px-3 py-2.5 font-mono text-sm focus:outline-none focus:border-ink/40"
              />
            </div>

            <div>
              <label htmlFor="goal-note" className="overline mb-2 block">
                Note (optional)
              </label>
              <textarea
                id="goal-note"
                data-testid="goal-note-input"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={2}
                placeholder="Why this matters…"
                className="w-full border border-paper-edge bg-paper-alt px-3 py-2.5 font-mono text-sm focus:outline-none focus:border-ink/40 resize-none"
              />
            </div>
          </div>

          <DialogFooter className="px-6 pb-6 flex gap-2">
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="btn-secondary text-xs"
              data-testid="goal-cancel"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving || !target || !deadline}
              className="btn-primary text-xs disabled:opacity-50"
              data-testid="goal-save"
            >
              {saving ? "Saving" : "Set milestone"}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default GoalDialog;
